import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonService, UserService } from '@tw/services';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { Checkbox } from 'primeng/checkbox';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { FloatLabel } from 'primeng/floatlabel';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitch } from 'primeng/toggleswitch';
import {
  PermissionAction,
  PermissionSubject,
} from 'src/shared/models/permissions';
import { SupersetService } from 'src/shared/services/superset.service';

type Superset = {
  id: number;
  name: string;
  dashboardId: string;
  isActive: true;
  createdBy: string;
  modifiedBy: string;
};

@Component({
  selector: 'tw-supersets',
  imports: [
    Button,
    Dialog,
    Checkbox,
    ReactiveFormsModule,
    InputText,
    TableModule,
    CommonModule,
    IconField,
    InputIcon,
    FloatLabel,
    ToggleSwitch,
    FormsModule,
    ConfirmDialog,
    ToastModule,
    CardModule,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './supersets.component.html',
})
export class SupersetsComponent implements OnInit {
  private userService = inject(UserService);
  private commonService = inject(CommonService);
  private formBuilder = inject(FormBuilder);

  cols = [
    { field: 'name', header: 'Name', type: 'text' },
    { field: 'dashboardId', header: 'Dashboard ID', type: 'text' },
    { field: 'isActive', header: 'Active', type: 'boolean' },
  ];
  showDialog = false;
  supersetForm!: FormGroup;
  supersets: Superset[] = [];
  isEdit = false;
  submitted = false;

  get CanAddSuperset() {
    return this.userService.hasPermission(
      PermissionSubject.SupersetReport,
      PermissionAction.Add,
    );
  }

  constructor(
    private supersetService: SupersetService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService,
  ) {}

  async ngOnInit() {
    this.initializeForm();
    this.loadSupersets();
  }

  initializeForm() {
    this.supersetForm = this.formBuilder.group({
      id: [null],
      name: ['', Validators.required],
      dashboardId: [null, Validators.required],
      isActive: [true],
    });
  }

  async loadSupersets() {
    this.supersets = await this.supersetService.getSuperset();
  }

  AddSuperset() {
    this.isEdit = false;
    this.showDialog = true;
    this.supersetForm.reset();
    this.supersetForm.patchValue({
      isActive: true,
    });
  }
  showDetails(superset: Superset) {
    if (!this.CanEditSuperset) return;
    this.isEdit = true;
    this.supersetForm.patchValue(superset);
    this.showDialog = true;
    this.cdr.detectChanges();
  }

  get CanEditSuperset() {
    return this.userService.hasPermission(
      PermissionSubject.SupersetReport,
      PermissionAction.Edit,
    );
  }

  async updateStatus(superset: any) {
    await this.supersetService.updateSuperset(superset.id, superset);
  }

  async submit() {
    this.submitted = true;
    const formData = this.supersetForm.getRawValue();
    if (!this.commonService.validateForm(this.supersetForm)) {
      this.commonService.showToast(
        'Error',
        'Please fill all the required values!',
      );
      return;
    }
    try {
      const newSuperset = {
        ...formData,
      };
      if (this.isEdit) {
        await this.supersetService.updateSuperset(
          this.supersetForm.value.id,
          newSuperset,
        );
        this.commonService.showToast(
          'Success',
          'Superset updated successfully!',
        );
      } else {
        await this.supersetService.createSuperset(newSuperset);
        this.commonService.showToast(
          'Success',
          'Superset created successfully!',
        );
      }
      this.supersetForm.reset();
      this.submitted = false;
      this.initializeForm();
      this.showDialog = false;
      this.loadSupersets();
      this.cdr.detectChanges();
    } catch (error: any) {
      error.error.errors.forEach((element: any) => {
        this.commonService.showToast('Error', element);
      });
    }
  }
  async onToggleChange(isActive: boolean, newValue: boolean, superset: any) {
    let message = '';
    if (isActive === true) {
      message = `Are you sure you want to ${newValue ? 'activate' : 'deactivate'} this superset?`;
    }
    this.confirmationService.confirm({
      message: message,
      header: 'Confirm Change',
      accept: async () => {
        this.isEdit = true;
        this.supersetForm.patchValue(superset);
        this.submit();
      },
      reject: () => {
        if (isActive === true) {
          const oldValue = superset.isActive;
          superset.isActive = !oldValue;
        }
      },
    });
  }
}
